// Maps Firebase Identity Platform auth error codes to human-readable copy so the
// sign-in UI never surfaces raw strings like "Firebase: Error (auth/invalid-credential)."

/** Best-effort extraction of a Firebase-style `.code` from an unknown throwable. */
export function authErrorCode(error: unknown): string | undefined {
  if (error && typeof error === 'object' && 'code' in error) {
    const code = (error as { code?: unknown }).code;
    if (typeof code === 'string') return code;
  }
  return undefined;
}

const FRIENDLY: Record<string, string> = {
  'auth/invalid-credential': 'Incorrect email or password.',
  'auth/wrong-password': 'Incorrect email or password.',
  'auth/user-not-found': 'No account found for that email.',
  'auth/invalid-email': 'That email address is not valid.',
  'auth/missing-password': 'Enter your password.',
  'auth/email-already-in-use':
    'This email is already registered — sign in instead, or reset your password.',
  'auth/weak-password': 'Password is too weak — use at least 6 characters.',
  'auth/user-disabled': 'This account has been disabled. Contact your administrator.',
  'auth/too-many-requests': 'Too many attempts. Please wait a moment and try again.',
  'auth/network-request-failed': 'Network error. Check your connection and try again.',
  'auth/operation-not-allowed': 'Email/password sign-in is not enabled for this project.',
};

/**
 * Human-readable message for an auth error. Unknown codes fall back to a clean
 * generic message — never the raw "Firebase: Error (...)." string.
 */
export function authErrorMessage(
  error: unknown,
  fallback = 'Something went wrong. Please try again.',
): string {
  const code = authErrorCode(error);
  return (code && FRIENDLY[code]) || fallback;
}
